import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material';
import { ArrowBack, CloudUpload, Close } from '@mui/icons-material';
import { Colors } from 'library';
import { MAX_UPLOAD_SIZE_MB, validateFileSize } from 'shared/fileValidation';
import { BaseButton } from './BaseButton';
import { Card } from './Card';
import { CheckboxRow } from './CheckboxRow';
import { ErrorText } from './ErrorText';
import { IconButton } from './IconButton';
import { Label } from './Label';
import { Link } from './Link';
import { ModalFooter } from './Modal';
import { PillButton } from './PillButton';
import { Row } from './Row';
import { Stack } from './Stack';
import { Text } from './Text';
import { Title } from './Title';

export const DEFAULT_ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
export const DEFAULT_MAX_FILE_SIZE_MB = MAX_UPLOAD_SIZE_MB;

export interface DocumentAttributeOption {
    name: string;
    description?: string | null;
}

interface DocumentUploadFormProps {
    selectedType: string;
    onSubmit: (file: File, attributes: string[]) => Promise<boolean>;
    onCancel: () => void;
    onChangeType?: (() => void) | null;
    acceptedFileTypes?: string;
    maxFileSizeMB?: number;
    description?: string;
    submitLabel?: string;
    uploadingLabel?: string;
    errorText?: string | null;
    loadAttributeTypes?: (typeName: string) => Promise<DocumentAttributeOption[]>;
    attributesTitle?: string;
}

export function DocumentUploadForm({
    selectedType,
    onSubmit,
    onCancel,
    onChangeType = null,
    acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
    maxFileSizeMB = DEFAULT_MAX_FILE_SIZE_MB,
    description,
    submitLabel = 'Upload',
    uploadingLabel = 'Uploading...',
    errorText,
    loadAttributeTypes,
    attributesTitle = 'Select the requirements this document meets',
}: DocumentUploadFormProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [attributeOptions, setAttributeOptions] = useState<DocumentAttributeOption[]>([]);
    const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);

    useEffect(() => {
        let active = true;
        setAttributeOptions([]);
        setSelectedAttributes([]);
        if (!loadAttributeTypes) return undefined;
        loadAttributeTypes(selectedType)
            .then((options) => {
                if (active) setAttributeOptions(options);
            })
            .catch(() => {
                if (active) setAttributeOptions([]);
            });
        return () => {
            active = false;
        };
    }, [loadAttributeTypes, selectedType]);

    function toggleAttribute(name: string, checked: boolean) {
        setSelectedAttributes((prev) => (checked ? [...prev, name] : prev.filter((n) => n !== name)));
    }

    const acceptedDisplay = acceptedFileTypes
        .split(',')
        .map((t) => t.replace('.', '').toUpperCase())
        .join(', ');

    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        const next = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (next) {
            const sizeError = validateFileSize(next, maxFileSizeMB);
            if (sizeError) {
                setError(sizeError);
                return;
            }
        }
        setError(null);
        setFile(next);
    }

    async function handleUpload() {
        if (!file) return;
        setUploading(true);
        setError(null);
        await onSubmit(file, selectedAttributes);
        setUploading(false);
    }

    return (
        <Stack gap="md">
            {onChangeType && (
                <Link
                    onClick={onChangeType}
                    weight="semibold"
                    underline={false}
                    color={Colors.secondary}
                    icon={<ArrowBack sx={{ fontSize: 18 }} />}
                >
                    Back
                </Link>
            )}

            <Card tone="muted" padding="xs">
                <Text weight="medium" tone="secondary">
                    {selectedType}
                </Text>
            </Card>

            {attributeOptions.length > 0 && (
                <Stack gap="sm">
                    <Label>{attributesTitle}</Label>
                    {attributeOptions.map((option) => (
                        <CheckboxRow
                            key={option.name}
                            checked={selectedAttributes.includes(option.name)}
                            onChange={(checked) => toggleAttribute(option.name, checked)}
                            label={option.name}
                            subtitle={option.description ?? undefined}
                        />
                    ))}
                </Stack>
            )}

            <Stack gap="none">
                <Label>
                    Upload Document <Text tone="error">*</Text>
                </Label>
                {description && (
                    <Text size="sm" tone="secondary">
                        {description}
                    </Text>
                )}
                {file ? (
                    <Card padding="sm">
                        <Row align="center" gap="sm">
                            <Stack grow gap="none">
                                <Text weight="semibold">{file.name}</Text>
                                <Text size="sm" tone="secondary">
                                    {formatBytes(file.size)}
                                </Text>
                            </Stack>
                            <IconButton
                                icon={Close}
                                onClick={() => setFile(null)}
                                disabled={uploading}
                                size={28}
                                iconSize={18}
                            />
                        </Row>
                    </Card>
                ) : (
                    <DropZone onClick={() => inputRef.current?.click()}>
                        <CloudUpload sx={{ fontSize: 32, color: Colors.textSecondary }} />
                        <Title size="h3">Click to choose a file</Title>
                        <Text tone="secondary" size="sm">
                            {acceptedDisplay} up to {maxFileSizeMB}MB
                        </Text>
                    </DropZone>
                )}
                <input ref={inputRef} type="file" accept={acceptedFileTypes} hidden onChange={handleFileChange} />
            </Stack>

            {(error || errorText) && <ErrorText>{error || errorText}</ErrorText>}

            <ModalFooter>
                <PillButton variant="secondary" onClick={onCancel} disabled={uploading}>
                    Cancel
                </PillButton>
                <PillButton onClick={handleUpload} disabled={!file || uploading} color={Colors.secondary}>
                    {uploading ? uploadingLabel : submitLabel}
                </PillButton>
            </ModalFooter>
        </Stack>
    );
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const DropZone = styled(BaseButton)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    padding: '48px 24px',
    border: `1.5px dashed ${Colors.border}`,
    borderRadius: 12,
    backgroundColor: Colors.surface,
    '&:hover': {
        borderColor: Colors.secondaryMuted,
        backgroundColor: Colors.secondarySoft,
    },
    '&:focus, &:focus-visible': {
        borderColor: Colors.secondaryMuted,
    },
});
